/* =========================================================================
   Workshop — theme.js
   Core interactions: drawers/overlay, mobile nav, sticky header,
   accordions, sliders, quantity inputs. No dependencies.
   ========================================================================= */
(function () {
  'use strict';

  const root = document.documentElement;
  const body = document.body;

  /* ---------- Overlay + drawer manager ---------- */
  const Drawers = {
    overlay: null,
    openEl: null,
    ensureOverlay() {
      if (this.overlay) return this.overlay;
      const el = document.createElement('div');
      el.className = 'overlay';
      el.setAttribute('data-overlay', '');
      body.appendChild(el);
      el.addEventListener('click', () => this.closeAll());
      this.overlay = el;
      return el;
    },
    open(drawer) {
      if (!drawer) return;
      this.ensureOverlay().classList.add('is-open');
      drawer.classList.add('is-open');
      drawer.removeAttribute('hidden');
      body.classList.add('overflow-hidden');
      this.openEl = drawer;
      const focusable = drawer.querySelector('[autofocus], button, a, input');
      if (focusable) setTimeout(() => focusable.focus(), 60);
      drawer.dispatchEvent(new CustomEvent('drawer:open'));
    },
    close(drawer) {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('hidden', '');
      if (this.overlay) this.overlay.classList.remove('is-open');
      body.classList.remove('overflow-hidden');
      this.openEl = null;
    },
    closeAll() {
      document.querySelectorAll('.drawer.is-open, .cart-drawer.is-open').forEach((d) => this.close(d));
      if (this.overlay) this.overlay.classList.remove('is-open');
      body.classList.remove('overflow-hidden');
    },
  };
  window.WorkshopDrawers = Drawers;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.querySelector(opener.getAttribute('data-drawer-open'));
      // No target drawer rendered (e.g. cart type 'page') → let links navigate normally
      if (drawer) {
        e.preventDefault();
        Drawers.open(drawer);
      }
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      Drawers.close(closer.closest('.drawer, .cart-drawer'));
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') Drawers.closeAll();
  });

  /* ---------- Sticky header show/hide ---------- */
  const header = document.querySelector('[data-header]');
  if (header) {
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('header--scrolled', y > 20);
      if (y > lastY && y > 300 && !Drawers.openEl) {
        header.classList.add('header--hidden');
      } else {
        header.classList.remove('header--hidden');
      }
      lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { window.requestAnimationFrame(onScroll); ticking = true; }
    }, { passive: true });
  }

  /* ---------- Quantity inputs ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    const wrap = btn.closest('.qty');
    const input = wrap.querySelector('input');
    const step = btn.getAttribute('data-qty-change') === 'up' ? 1 : -1;
    const min = parseInt(input.min || '0', 10);
    let val = parseInt(input.value || '1', 10) + step;
    if (val < min) val = min;
    input.value = val;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Generic sliders (scroll-snap + arrows + dots) ---------- */
  function initSlider(sliderEl) {
    const track = sliderEl.querySelector('[data-slider-track]');
    if (!track) return;
    const slides = Array.from(track.children);
    const prev = sliderEl.querySelector('[data-slider-prev]');
    const next = sliderEl.querySelector('[data-slider-next]');
    const dotsWrap = sliderEl.querySelector('[data-slider-dots]');
    const autoplay = parseInt(sliderEl.getAttribute('data-autoplay') || '0', 10);
    let index = 0;
    let timer = null;

    function goTo(i, smooth = true) {
      index = (i + slides.length) % slides.length;
      const target = slides[index];
      track.scrollTo({ left: target.offsetLeft - track.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      if (dotsWrap) dotsWrap.querySelectorAll('button').forEach((d, di) => d.setAttribute('aria-current', di === index ? 'true' : 'false'));
    }
    if (prev) prev.addEventListener('click', () => goTo(index - 1));
    if (next) next.addEventListener('click', () => goTo(index + 1));

    if (dotsWrap) {
      slides.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', () => goTo(i));
        dotsWrap.appendChild(dot);
      });
      goTo(0, false);
    }

    if (autoplay > 0 && slides.length > 1) {
      const start = () => { timer = setInterval(() => goTo(index + 1), autoplay); };
      const stop = () => clearInterval(timer);
      start();
      sliderEl.addEventListener('mouseenter', stop);
      sliderEl.addEventListener('mouseleave', start);
      sliderEl.addEventListener('focusin', stop);
    }

    // update active dot on manual scroll
    let scrollT;
    track.addEventListener('scroll', () => {
      clearTimeout(scrollT);
      scrollT = setTimeout(() => {
        const mid = track.scrollLeft + track.clientWidth / 2;
        let closest = 0, dist = Infinity;
        slides.forEach((s, i) => {
          const c = s.offsetLeft - track.offsetLeft + s.clientWidth / 2;
          if (Math.abs(c - mid) < dist) { dist = Math.abs(c - mid); closest = i; }
        });
        index = closest;
        if (dotsWrap) dotsWrap.querySelectorAll('button').forEach((d, di) => d.setAttribute('aria-current', di === index ? 'true' : 'false'));
      }, 90);
    }, { passive: true });
  }
  document.querySelectorAll('[data-slider]').forEach(initSlider);
  window.WorkshopInitSlider = initSlider;

  /* ---------- Theme editor re-init ---------- */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      e.target.querySelectorAll('[data-slider]').forEach(initSlider);
    });
  }
})();
